import { Controller } from "@hotwired/stimulus"

// The list floats above the dialog (fixed, placed from the field's position) so opening it never makes the dialog body
// scroll; it opens upward when there is no room below.
function place(panel, anchor) {
  const rect = anchor.getBoundingClientRect()
  const room = window.innerHeight - rect.bottom
  const height = Math.min(panel.scrollHeight, 288)
  panel.style.position = "fixed"
  panel.style.left = `${rect.left}px`
  panel.style.width = `${rect.width}px`
  panel.style.right = "auto"
  if (room < height + 16 && rect.top > room) {
    panel.style.top = "auto"
    panel.style.bottom = `${window.innerHeight - rect.top + 4}px`
  } else {
    panel.style.bottom = "auto"
    panel.style.top = `${rect.bottom + 4}px`
  }
}

// A searchable SKU field: type to narrow the list (matches the SKU or the product title), click or press Enter to pick.
// Only a listed ASIN can be picked.
export default class extends Controller {
  static targets = ["input", "value", "list", "option", "none", "clear"]

  connect() {
    this.active = -1
    this.toggleClear()
  }

  open() {
    this.filter()
    this.show()
  }

  show() {
    this.listTarget.hidden = false
    place(this.listTarget, this.inputTarget.closest(".input-group"))
    this.inputTarget.setAttribute("aria-expanded", "true")
    if (!this.reposition) {
      this.reposition = () => place(this.listTarget, this.inputTarget.closest(".input-group"))
      window.addEventListener("resize", this.reposition)
      window.addEventListener("scroll", this.reposition, true)
    }
  }

  disconnect() {
    this.close()
  }

  close() {
    if (this.reposition) {
      window.removeEventListener("resize", this.reposition)
      window.removeEventListener("scroll", this.reposition, true)
      this.reposition = null
    }
    this.listTarget.hidden = true
    this.inputTarget.setAttribute("aria-expanded", "false")
    this.active = -1
  }

  closeOutside(event) {
    if (!this.element.contains(event.target)) this.close()
  }

  filter() {
    // Emptying the box means "whole account"; anything else only counts once an option is picked.
    if (!this.inputTarget.value.trim()) this.valueTarget.value = ""
    const needle = this.inputTarget.value.trim().toLowerCase()
    let shown = 0
    this.optionTargets.forEach((option) => {
      const match = !needle || option.dataset.search.includes(needle)
      option.hidden = !match
      option.classList.remove("is-active")
      if (match) shown++
    })
    this.noneTarget.hidden = shown > 0
    this.active = -1
    this.show()
    this.toggleClear()
  }

  // mousedown, not click: it fires before the input loses focus, so the list is still there to pick from.
  choose(event) {
    event.preventDefault()
    this.select(event.currentTarget.dataset.value, event.currentTarget.dataset.label)
  }

  // The box shows the ASIN people recognise; the SKU the expense is stored against rides in the hidden field.
  select(value, label = value) {
    this.valueTarget.value = value
    this.inputTarget.value = label
    this.close()
    this.toggleClear()
  }

  clear() {
    this.valueTarget.value = ""
    this.inputTarget.value = ""
    this.filter()
    this.inputTarget.focus()
  }

  keydown(event) {
    const visible = this.optionTargets.filter((option) => !option.hidden)
    if (event.key === "Escape") {
      if (!this.listTarget.hidden) { event.stopPropagation(); this.close() }
    } else if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault()
      if (this.listTarget.hidden) this.open()
      if (!visible.length) return
      this.active = (this.active + (event.key === "ArrowDown" ? 1 : -1) + visible.length) % visible.length
      visible.forEach((option, i) => option.classList.toggle("is-active", i === this.active))
      visible[this.active].scrollIntoView({ block: "nearest" })
    } else if (event.key === "Enter" && this.active >= 0 && !this.listTarget.hidden) {
      event.preventDefault()
      this.select(visible[this.active].dataset.value, visible[this.active].dataset.label)
    }
  }

  toggleClear() {
    this.clearTarget.hidden = this.inputTarget.value === ""
  }
}
